import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ProgressProduct } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { IGroupedData } from "@common/lib/utilities/grouped-data.interface";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { IProgressProductImportSource, ProgressService } from "@common/progress/progress.service";
import { UserService } from "@common/user/user.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { catchError, EMPTY, finalize, forkJoin, of, Subscription, tap } from "rxjs";

type SelectableProgressProduct = IProgressProductImportSource & { disabled: boolean };

@Component({
    selector: "adapt-select-progress-products",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./select-progress-products.component.html",
    styleUrls: ["./select-progress-products.component.css"],
})
export class SelectProgressProductsComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() public progressProductIds: number[] = [];
    @Output() public progressProductIdsChange = new EventEmitter<number[]>();
    public existingProducts: IProgressProductImportSource[] = [];
    public groupedProgressProducts: IGroupedData<string, SelectableProgressProduct>[] = [];
    public isLoading = true;
    public errorMessage?: string;
    @Input() public organisation?: Organisation;

    private loadSubscription?: Subscription;
    private existingNames = new Set<string>();

    public constructor(private progressService: ProgressService, private userService: UserService) {
        super();
    }

    public get showCodes() {
        return !!this.userService.currentPerson?.isStakeholderManager;
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.organisation && !changes.organisation.firstChange) {
            this.loadProducts();
        } else if (changes.progressProductIds) {
            this.updateSelection();
        }
    }

    public ngOnInit() {
        this.loadProducts();
    }

    @Autobind
    public progressProductDisplayExpr(row: Pick<ProgressProduct, "name" | "code">) {
        return this.showCodes ? `${row.name} (${row.code ?? "N/A"})` : row.name;
    }

    @Autobind
    public progressProductTagText(row: IProgressProductImportSource) {
        const name = this.progressProductDisplayExpr(row);
        return row.organisationId ? `${name} (${row.organisationName ?? row.organisationId})` : name;
    }

    public onValueChanged(values: number[]) {
        if (values.length === this.progressProductIds.length && values.every((id, index) => id === this.progressProductIds[index])) {
            return;
        }
        this.progressProductIds = values;
        this.updateSelection();
        this.progressProductIdsChange.emit(values);
    }

    private loadProducts() {
        this.loadSubscription?.unsubscribe();
        this.isLoading = true;
        this.errorMessage = undefined;
        this.loadSubscription = forkJoin({
            sources: this.progressService.getImportableProgressProducts(),
            existing: this.organisation ? this.progressService.getProgressProducts(this.organisation.organisationId) : of([]),
        }).pipe(
            tap(({ sources, existing }) => {
                const products = sources
                    .filter((product) => !product.organisationId || product.organisationId !== this.organisation?.organisationId)
                    .map((product) => ({ ...product, disabled: false }))
                    .sort(SortUtilities.getSortByFieldFunction<IProgressProductImportSource>("ordinal"));
                this.existingNames = new Set(existing.map(ProgressService.getImportNameKey));
                this.groupedProgressProducts = ArrayUtilities.groupArrayBy(products,
                    (product) => product.organisationId
                        ? `Organisation: ${product.organisationName ?? product.organisationId} (${product.organisationUrlIdentifier ?? product.organisationId})`
                        : product.partnerId ? "Partner" : "Default")
                    // move partner entries before organisations
                    .sort(SortUtilities.getSortByFieldFunction<IGroupedData<string, SelectableProgressProduct>>(
                        (group) => group.key.startsWith("Organisation:"),
                        (group) => group.key,
                    ));
                const availableIds = new Set(products.map((product) => product.progressProductId));
                const selectedIds = this.progressProductIds.filter((id) => availableIds.has(id));
                if (selectedIds.length !== this.progressProductIds.length) {
                    this.progressProductIds = selectedIds;
                    this.progressProductIdsChange.emit(selectedIds);
                }
                this.updateSelection();
            }),
            catchError((error) => {
                this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(error);
                return EMPTY;
            }),
            finalize(() => this.isLoading = false),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private updateSelection() {
        const selectedIds = new Set(this.progressProductIds);
        const selectedProducts = this.groupedProgressProducts.flatMap((group) => group.items)
            .filter((product) => selectedIds.has(product.progressProductId));
        const selectedNameCounts = new Map<string, number>();
        for (const product of selectedProducts) {
            const name = ProgressService.getImportNameKey(product);
            selectedNameCounts.set(name, (selectedNameCounts.get(name) ?? 0) + 1);
        }

        this.existingProducts = selectedProducts.filter((product) => {
            const name = ProgressService.getImportNameKey(product);
            return this.existingNames.has(name) || selectedNameCounts.get(name)! > 1;
        });
        this.groupedProgressProducts = this.groupedProgressProducts.map((group) => ({
            key: group.key,
            items: group.items.map((product) => {
                const name = ProgressService.getImportNameKey(product);
                return {
                    ...product,
                    disabled: !selectedIds.has(product.progressProductId) && (this.existingNames.has(name) || selectedNameCounts.has(name)),
                };
            }),
        }));
    }
}
